import { NotationToDecimalValueType } from "./interfaces and Enums/TYPENotations";
import Big from "big.js";

/**
 * Checks if a notation represents a currency symbol that should be prepended.
 * Currency notations: "$", "£", "€", "¥", or multi-char currency codes like "AUD "
 * NOT currency: "%", "bps", "$fr", "$p", "c", "strike"
 */
export function isCurrencyNotation(notation: string): boolean {
  if (!notation) return false;
  if (notation === "$" || notation === "£" || notation === "€" || notation === "¥") return true;
  // Multi-char currency codes from getCurrencySymbol have a trailing space (e.g., "AUD ")
  if (notation.length > 1 && notation.endsWith(" ") && !notation.startsWith("$")) return true;
  return false;
}

/**
 * Checks if a notation is a price-type notation that should NOT display a suffix.
 * "$p" and "$fr" are price notations — their suffix is suppressed in display,
 * and when fields are empty, "price" is shown as a placeholder instead.
 */
export function isPriceNotation(notation: string | undefined): boolean {
  return notation === "$p" || notation === "$fr";
}

/**
 * Formats a value with its notation for display.
 * Currency notations ($, £, €, ¥, AUD, etc.) are prepended: "$4.50", "-£4.50"
 * Non-currency notations (%, bps) are appended: "4.50 %", "15 bps"
 * Price notations ($p, $fr) show value only — no suffix displayed.
 */
export function formatValueWithNotation(
  value: number | string | null | undefined,
  notation: NotationToDecimalValueType | string | undefined,
): string {
  if (value === null || value === undefined || value === "") {
    return "";
  }
  const notationStr = notation || "";

  if (!notationStr) {
    return String(value);
  }

  if (isCurrencyNotation(notationStr)) {
    const isNegative = Number(value) < 0;
    const absValue = String(value).replace(/^-/, "");
    return isNegative ? `-${notationStr}${absValue}` : `${notationStr}${absValue}`;
  }

  // Price notations ($p, $fr): show value WITHOUT suffix
  if (isPriceNotation(notationStr)) {
    return String(value);
  }

  return `${value} ${notationStr}`;
}

export function formatNumberBasedOnValue(
  type?: NotationToDecimalValueType,
): number {
  switch (type) {
    case "c":
      return 8;
    case "$fr":
      return 8; // was 10 - Task  14758
    case "strike": // used instead of a notation
      return 8; // was 10 - Task  14758

    case "bps":
      return 6;
    case "%":
      return 6; // was 8 - Task  14758
    case "$p":
      return 6; // was 10 - Task  14758

    case "$":
      return 2;

    default:
      return 6;
  }
}

export function returnDecimalFormatting(
  value: number,
  type?: NotationToDecimalValueType,
  preventCommasInString?: boolean,
) {
  // Guard against non-numeric values
  if (value == null || typeof value !== "number" || isNaN(value)) {
    return "";
  }

  let numberOfDecimalPlaces;

  if (type) {
    numberOfDecimalPlaces = formatNumberBasedOnValue(type);
  } else {
    numberOfDecimalPlaces = 6;
  }

  // const jsFixed = (v, dp) => Number(v).toFixed(dp); // native JS rounding
  // const bigUp = (v, dp) => new Big(v).toFixed(dp, Big.roundUp); // Big.js "always up"
  const decimalFormatted = parseFloat(
    new Big(value).toFixed(numberOfDecimalPlaces, Big.roundHalfUp),
  );
  let returnValue = decimalFormatted.toString().split(".");
  if (type == "$") {
    returnValue = decimalFormatted.toFixed(2).split("."); // Don't strip 0's for money.
  }
  if (preventCommasInString == true) {
    return returnValue.join(".");
  }
  returnValue[0] = returnValue[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return returnValue.join(".");
}

export const MONTH_STRING = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// LQC-2575 - returnFormattedDate builds the string "NaN-undefined-NaN" out of
// anything it cannot parse: a missing date, or a value that has already been through
// it once. AG Grid hands a group row the formatted key and re-runs the formatter over
// it, so grouping a date column hits exactly that. Thin wrapper - shows the raw text
// if it is not parseable, and nothing at all when there is no date.
export function returnFormattedDateSafe(
  value: number | string | Date | null | undefined,
): string {
  if (value == null || value === "") return "";
  const formatted = returnFormattedDate(value);
  return formatted.includes("NaN") ? String(value) : formatted;
}

export function returnFormattedDate(inDate: number | string | Date) {
  if (typeof inDate === "string" && !inDate.includes("T")) {
    inDate += "T00:00";
  }
  
  const date = new Date(inDate);

  const day = date.getDate().toString().padStart(2, "0");

  const month = MONTH_STRING[date.getMonth()];

  const year = date.getFullYear().toString();

  const dateFormatted = day + "-" + month + "-" + year;

  return dateFormatted;
}

// Format Date times to string for presenting values
export function returnStringFormattedDateTime(
  dateTime: number | string | Date,
) {
  //Determien if time is in dateTime
  const hasTime =
    typeof dateTime === "number" ||
    dateTime instanceof Date ||
    (typeof dateTime === "string" && /T|\d{2}:\d{2}/.test(dateTime));

  const date = new Date(dateTime);

  const day = date.getDate().toString().padStart(2, "0");

  const month = MONTH_STRING[date.getMonth()];

  const year = date.getFullYear().toString();

  const dateFormatted = day + "-" + month + "-" + year;

  if (!hasTime) return dateFormatted;

  const timeFormatted = date.toTimeString().slice(0, 8);
  return dateFormatted + " " + timeFormatted;
}

// Null-safe wrapper. Optional timestamp fields (e.g. expiryEnd) are often
// absent, and a grid cell should be blank rather than read "Invalid Date".
export function returnStringFormattedDateTimeOrEmpty(
  dateTime: number | string | Date | null | undefined,
): string {
  if (!dateTime) return "";
  if (isNaN(new Date(dateTime).getTime())) return "";
  return returnStringFormattedDateTime(dateTime);
}

// Same output to minute precision (`12-Aug-2026 09:00`). Used by the expiry
// column's date tier, where seconds on a timestamp days away are noise.
export function returnStringFormattedDateTimeToMinute(
  dateTime: number | string | Date,
): string {
  return returnStringFormattedDateTime(dateTime).replace(
    /(\d{2}:\d{2}):\d{2}$/,
    "$1",
  );
}

// The API sends some timestamps WITHOUT a timezone designator, e.g. expiryEnd
// as "2026-08-06T10:24:50" while actionedAt in the same payload is
// "...Z". JavaScript parses a naked date-time as LOCAL, so those values render
// with the UTC digits shown raw (10 hours out in AEST). The server values are
// UTC, so treat a missing designator as UTC.
//
// Only touches strings that have a time and no designator, so it is a no-op on
// anything already correct - including after the backend is fixed to send an
// offset. Date-only strings are left alone (returnFormattedDate deliberately
// reads those as local so a settlement date can't shift a day).
export function parseServerDateTime(
  dateTime: number | string | Date | null | undefined,
): Date | null {
  if (!dateTime) return null;

  let parsed: Date;
  if (typeof dateTime === "string") {
    const hasTime = /T\d{2}:\d{2}/.test(dateTime);
    const hasDesignator = /(Z|[+-]\d{2}:?\d{2})$/.test(dateTime);
    parsed = new Date(hasTime && !hasDesignator ? `${dateTime}Z` : dateTime);
  } else {
    parsed = new Date(dateTime);
  }

  return isNaN(parsed.getTime()) ? null : parsed;
}

export function returnPercentageFormatted(inputValue: number) {
  return Math.floor(inputValue).toFixed(0);
}

export function returnSizeFormatted(value: number, instrumentType?: string) {
  if (value <= 0) return "0";

  if (instrumentType && instrumentType == "FUT") {
    return value.toString() + " lots";
  }

  // return Math.abs(value) >= 1.0e6
  //   ? (Math.abs(value) / 1.0e6).toString() + " MM"
  //   : Math.abs(value) >= 1.0e3
  //     ? (Math.abs(value) / 1.0e3).toString() + " M"
  //     : Math.abs(value).toString();

  const splits = value.toString().split(".");
  // Floating point error needed to be addressed with rounding. 14649
  let addedDecimalLength = 0;
  if (splits.length == 2) {
    addedDecimalLength = splits[1].length;
  }

  if (Math.abs(value) >= 1.0e6) {
    // Format millions.
    return (
      // parse float will remove 0s
      parseFloat((Math.abs(value) / 1.0e6).toFixed(6 + addedDecimalLength)) +
      " MM"
    );
  } else if (Math.abs(value) >= 1.0e3) {
    // Format thousands.
    return (
      // parse float will remove 0s
      parseFloat((Math.abs(value) / 1.0e3).toFixed(3 + addedDecimalLength)) +
      " M"
    );
  }
  return Math.abs(value).toString();
}

export function returnDV0_Formatted(value: number) {
  return Math.abs(value) >= 1.0e3
    ? (Math.abs(value) / 1.0e3).toFixed(0).toString() + " k"
    : Math.abs(value).toFixed(0).toString();
}

export function returnDirectionFormatted(value: number, entity: string) {
  return entity + " " + value;
}

// Converting numbers to OrdinalNumbers - EG 1 = 1st.
export function toOrdinal(n: number): string {
  // Handle the few special cases directly
  const lastTwo = n % 100;
  if (lastTwo >= 11 && lastTwo <= 13) {
    return `${n}th`;
  }
  // use 9999th as special case and return ""
  if (n == 9999) {
    return "";
  }

  // Handle the normal endings
  const lastDigit = n % 10;
  if (lastDigit === 1) return `${n}st`;
  if (lastDigit === 2) return `${n}nd`;
  if (lastDigit === 3) return `${n}rd`;
  return `${n}th`;
}
